import { useState, useRef, useEffect, useCallback, KeyboardEvent } from 'react';
import styled from 'styled-components';

interface Props {
    value: string;
    onChange: (newValue: string) => void;
    placeholder?: string;
}

const Styles = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    width: 100%;
    min-width: 0;

    .display-text {
        cursor: default;
        flex: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .edit-icon {
        cursor: pointer;
        opacity: 0;
        transition: opacity 0.15s;
        color: var(--ct-text-muted);
        flex-shrink: 0;

        &:hover {
            color: var(--ct-primary);
        }
    }

    &:hover .edit-icon {
        opacity: 1;
    }

    .edit-input {
        border: 1px solid var(--ct-primary);
        border-radius: 4px;
        padding: 2px 6px;
        font-size: inherit;
        outline: none;
        width: 90%;

        &:focus {
            box-shadow: 0 0 0 2px rgba(var(--ct-primary-rgb), 0.25);
        }
    }

    &.editing {
        width: 90%;
    }
`;

const InlineEditableText = ({ value, onChange, placeholder = '(untitled)' }: Props) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(value);
    const inputRef = useRef<HTMLInputElement>(null);

    // Focus input when entering edit mode
    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    // Update edit value when prop changes
    useEffect(() => {
        if (!isEditing) {
            setEditValue(value);
        }
    }, [value, isEditing]);

    const handleEdit = useCallback((e: React.MouseEvent) => {
        e.stopPropagation();
        setEditValue(value);
        setIsEditing(true);
    }, [value]);

    const handleSave = useCallback(() => {
        const trimmed = editValue.trim();
        if (trimmed && trimmed !== value) {
            onChange(trimmed);
        }
        setIsEditing(false);
    }, [editValue, value, onChange]);

    const handleCancel = useCallback(() => {
        setEditValue(value);
        setIsEditing(false);
    }, [value]);

    const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSave();
        } else if (e.key === 'Escape') {
            e.preventDefault();
            handleCancel();
        }
    }, [handleSave, handleCancel]);

    const handleBlur = useCallback(() => {
        // Save on blur
        handleSave();
    }, [handleSave]);

    if (isEditing) {
        return (
            <Styles className="editing" onClick={(e) => e.stopPropagation()}>
                <input
                    ref={inputRef}
                    type="text"
                    className="edit-input"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    onBlur={handleBlur}
                />
            </Styles>
        );
    }

    return (
        <Styles>
            <span className="display-text" title={value}>{value || placeholder}</span>
            <span className="edit-icon" onClick={handleEdit} title="Rename">
                <i className="mdi mdi-pencil"></i>
            </span>
        </Styles>
    );
};

export default InlineEditableText;
